"use client";

import {
  useCallback,
  useEffect,
  useId,
  useState,
  type FormEvent,
  type KeyboardEvent as ReactKeyboardEvent,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";
import { useRouter } from "next/navigation";
import { useAuth } from "@/contexts/AuthContext";
import { useLanguage } from "@/contexts/LanguageContext";
import { requestGetOtp, requestVerifyOtp } from "@/lib/authClient";

const OTP_LENGTH = 4;
const emptyOtp = () => Array.from({ length: OTP_LENGTH }, () => "");

export type ContactPhoneOtpModalProps = {
  open: boolean;
  onClose: () => void;
  /** E.164-style string without spaces, e.g. `+966501234567` */
  fullPhone: string;
  onVerified: (verifiedPhoneE164: string) => void;
};

export function ContactPhoneOtpModal({
  open,
  onClose,
  fullPhone,
  onVerified,
}: ContactPhoneOtpModalProps) {
  const router = useRouter();
  const { login } = useAuth();
  const { locale, t } = useLanguage();
  const languageCode: "en" | "ar" = locale === "ar" ? "ar" : "en";
  const [otp, setOtp] = useState<string[]>(() => emptyOtp());
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [resendCooldown, setResendCooldown] = useState(0);
  const otpBaseId = useId().replace(/:/g, "");
  const titleId = useId();

  const startResendCooldown = useCallback(() => {
    setResendCooldown(60);
    const interval = setInterval(() => {
      setResendCooldown((c) => {
        if (c <= 1) {
          clearInterval(interval);
          return 0;
        }
        return c - 1;
      });
    }, 1000);
  }, []);

  useEffect(() => {
    if (!open) return;
    setError("");
    setIsLoading(false);
    setResendCooldown(0);
    setOtp(emptyOtp());
    const id = requestAnimationFrame(() => {
      document.getElementById(`${otpBaseId}-otp-0`)?.focus();
    });
    return () => cancelAnimationFrame(id);
  }, [open, otpBaseId]);

  useEffect(() => {
    if (!open) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e: globalThis.KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener("keydown", onKey);
    };
  }, [open, onClose]);

  const handleOtpChange = (index: number, value: string) => {
    if (value.length > 1) {
      const digits = value.replace(/\D/g, "").slice(0, OTP_LENGTH).split("");
      const newOtp = [...otp];
      digits.forEach((d, i) => {
        if (index + i < OTP_LENGTH) newOtp[index + i] = d;
      });
      setOtp(newOtp);
      const next = document.getElementById(
        `${otpBaseId}-otp-${Math.min(index + digits.length, OTP_LENGTH - 1)}`
      );
      (next as HTMLInputElement)?.focus();
      return;
    }
    const digit = value.replace(/\D/g, "").slice(-1);
    const newOtp = [...otp];
    newOtp[index] = digit;
    setOtp(newOtp);
    if (digit && index < OTP_LENGTH - 1) {
      const next = document.getElementById(`${otpBaseId}-otp-${index + 1}`);
      (next as HTMLInputElement)?.focus();
    }
  };

  const handleOtpKeyDown = (index: number, e: ReactKeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      const prev = document.getElementById(`${otpBaseId}-otp-${index - 1}`);
      (prev as HTMLInputElement)?.focus();
      const newOtp = [...otp];
      newOtp[index - 1] = "";
      setOtp(newOtp);
    }
  };

  const handleVerifyOtp = async (e: FormEvent) => {
    e.preventDefault();
    setError("");
    const code = otp.join("");
    if (code.length !== OTP_LENGTH) {
      setError(t("login.errorOtp"));
      return;
    }
    setIsLoading(true);
    try {
      const result = await requestVerifyOtp(fullPhone, code);
      if (!result.ok) {
        setError(result.message);
        return;
      }
      login(result.user);
      router.refresh();
      onVerified(fullPhone);
      onClose();
    } catch {
      setError(t("login.errorInvalidOtp"));
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendOtp = async () => {
    if (resendCooldown > 0) return;
    setError("");
    setIsLoading(true);
    try {
      const { ok, message } = await requestGetOtp(fullPhone, languageCode);
      if (!ok) {
        setError(message);
        return;
      }
      startResendCooldown();
    } catch {
      setError(t("login.errorResend"));
    } finally {
      setIsLoading(false);
    }
  };

  const title = t("contact.verifyPhoneTitle");
  const closeLabel = t("contact.closePhoneVerifyModal");

  const inner: ReactNode = (
    <>
      <div className="flex items-center justify-between gap-3 border-b border-[#E5E5E5] px-5 py-4">
        <h2 id={titleId} className="text-base font-semibold text-[#130F0E]">
          {title}
        </h2>
        <button
          type="button"
          onClick={onClose}
          className="flex h-9 w-9 items-center justify-center rounded-lg text-[#6B7280] hover:bg-[#F4F4F4]"
          aria-label={closeLabel}
        >
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            aria-hidden
          >
            <path d="M18 6L6 18M6 6l12 12" />
          </svg>
        </button>
      </div>
      <div className="max-h-[min(75vh,28rem)] overflow-y-auto p-5">
        <p className="mb-6 text-sm text-[#6B7280]">
          {`${t("contact.verifyPhoneOtpSubtext")} ${fullPhone}`}
        </p>

        {error ? (
          <div className="mb-6 rounded-lg bg-red-50 p-3 text-sm text-red-600">
            {error}
          </div>
        ) : null}

        <form onSubmit={handleVerifyOtp} className="space-y-6">
          <div>
            <label className="mb-3 block text-sm font-medium text-[#130F0E]">
              {t("login.otpLabel")}
            </label>
            <div className="flex justify-center gap-2">
              {otp.map((digit, i) => (
                <input
                  key={i}
                  id={`${otpBaseId}-otp-${i}`}
                  type="text"
                  inputMode="numeric"
                  maxLength={i === 0 ? OTP_LENGTH : 1}
                  value={digit}
                  onChange={(e) => handleOtpChange(i, e.target.value)}
                  onKeyDown={(e) => handleOtpKeyDown(i, e)}
                  autoComplete={i === 0 ? "one-time-code" : "off"}
                  aria-label={`OTP digit ${i + 1} of ${OTP_LENGTH}`}
                  className="h-14 w-12 rounded-lg border border-[#E5E5E5] text-center text-xl font-bold text-[#130F0E] focus:border-[#DD0428] focus:outline-none focus:ring-2 focus:ring-[#DD0428]/20"
                />
              ))}
            </div>
          </div>
          <button
            type="submit"
            disabled={isLoading}
            className="w-full rounded-lg bg-[#BD2226] py-3.5 font-semibold text-white transition-colors hover:bg-[#C00322] disabled:cursor-not-allowed disabled:opacity-70"
          >
            {isLoading ? t("login.verifying") : t("contact.verifyOtpSubmit")}
          </button>
          <div className="text-center">
            <button
              type="button"
              onClick={handleResendOtp}
              disabled={resendCooldown > 0 || isLoading}
              className="text-sm font-medium text-[#BD2226] hover:underline disabled:cursor-not-allowed disabled:opacity-50 disabled:no-underline"
            >
              {resendCooldown > 0
                ? `${t("login.resendIn")} ${resendCooldown}${t("login.secondsAbbrev")}`
                : t("login.resendOtp")}
            </button>
          </div>
        </form>
      </div>
    </>
  );

  if (!open || typeof document === "undefined") return null;

  return createPortal(
    <div className="fixed inset-0 z-[120] flex items-end justify-center md:items-center md:p-4">
      <button
        type="button"
        className="absolute inset-0 bg-black/45 backdrop-blur-[1px]"
        aria-label={closeLabel}
        onClick={onClose}
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className="relative z-10 mt-auto flex max-h-[min(88vh,36rem)] w-full max-w-[28rem] flex-col overflow-hidden rounded-t-2xl border border-[#E5E7EB] bg-white shadow-2xl md:mt-0 md:rounded-2xl"
      >
        {inner}
      </div>
    </div>,
    document.body
  );
}
